import { ChangeEvent, useEffect, useState } from 'react';
import {
  ArrowRight,
  Bot,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileCheck,
  FileText,
  Globe,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Sparkles,
  Target,
  User2,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { appId, db, storage } from '@/lib/firebase';

interface CustomerDashboardPageProps {
  displayName: string;
  userId: string;
  userEmail?: string | null;
  /** When provided, "Book a meeting" CTA opens the Strategy Call modal. */
  onOpenStrategyCall?: () => void;
  /** Reserved for future backend/Cloud Function notification. */
  websiteRequestNotificationEmail?: string | null;
  /** Reserved for future backend/Cloud Function. */
  brevoSenderName?: string | null;
  /** Reserved for future backend/Cloud Function. */
  brevoSenderEmail?: string | null;
}

const quickActionsConfig = [
  {
    id: 'opportunities' as const,
    title: 'Review opportunities',
    description: 'Browse matched contract opportunities and shortlist your next targets.',
    icon: Target,
    cta: 'Explore opportunities',
    comingSoon: true,
  },
  {
    id: 'profile' as const,
    title: 'Update profile',
    description: 'Keep your capability profile current so recommendations stay relevant.',
    icon: Users,
    cta: 'Go to profile',
    comingSoon: false,
  },
  {
    id: 'outreach-notes' as const,
    title: 'Prepare outreach notes',
    description: 'Track strategic notes for prime conversations and follow-up priorities.',
    icon: FileText,
    cta: 'Open notes flow',
    comingSoon: true,
  },
];

const processStepsConfig = [
  {
    number: 1,
    title: 'Capability Review',
    description: 'Capability alignment before outreach',
    icon: FileCheck,
  },
  {
    number: 2,
    title: 'Prime Alignment',
    description: 'Contract vehicle relevance',
    icon: Target,
  },
  {
    number: 3,
    title: 'Targeted Prime ID',
    description: 'Targeted Prime identification',
    icon: Users,
  },
  {
    number: 4,
    title: 'Professional Outreach',
    description: 'Professional messaging and positioning',
    icon: Mail,
  },
  {
    number: 5,
    title: 'Structured Follow-up',
    description: 'Structured follow-up cadence',
    icon: ClipboardList,
  },
  {
    number: 6,
    title: 'Meeting Prep',
    description: 'Meeting preparation support before every call',
    icon: Calendar,
  },
];

type ReadinessStatus = 'submitted' | 'in_review' | 'scanning' | 'completed';

interface ReadinessScanRecord {
  websiteUrl: string;
  websiteHost: string;
  email: string | null;
  source: string;
  status: ReadinessStatus;
  progressPercent: number;
}

const normalizeWebsiteUrl = (rawUrl: string): string => {
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const statusLabelMap: Record<ReadinessStatus, string> = {
  submitted: 'Submitted',
  in_review: 'In review',
  scanning: 'Scanning',
  completed: 'Completed',
};

const statusDefaultProgress: Record<ReadinessStatus, number> = {
  submitted: 20,
  in_review: 45,
  scanning: 75,
  completed: 100,
};

export function CustomerDashboardPage({
  displayName,
  userId,
  userEmail,
  onOpenStrategyCall,
  websiteRequestNotificationEmail: _websiteRequestNotificationEmail,
  brevoSenderName: _brevoSenderName,
  brevoSenderEmail: _brevoSenderEmail,
}: CustomerDashboardPageProps): JSX.Element {
  const [websiteInput, setWebsiteInput] = useState('');
  const [isSubmittingScan, setIsSubmittingScan] = useState(false);
  const [isScanLoading, setIsScanLoading] = useState(true);
  const [scanRecord, setScanRecord] = useState<ReadinessScanRecord | null>(null);
  const [scanMessage, setScanMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isServiceSubmitting, setIsServiceSubmitting] = useState(false);
  const [serviceSubmitMessage, setServiceSubmitMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [capabilityStatementFile, setCapabilityStatementFile] = useState<File | null>(null);
  const [isProcessExpanded, setIsProcessExpanded] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    companyName: '',
    contactName: '',
    contactEmail: userEmail ?? '',
    contactPhone: '',
    websiteUrl: '',
    requestType: 'new_website' as
      | 'new_website'
      | 'website_enhancement'
      | 'redesign_and_enhancement',
    briefRequest: '',
  });

  useEffect(() => {
    const scanRef = doc(db, 'artifacts', appId, 'users', userId, 'federalReadinessScan', 'main');
    const unsubscribe = onSnapshot(
      scanRef,
      (docSnap) => {
        setScanMessage((prev) => (prev?.type === 'error' ? null : prev));
        setIsScanLoading(false);
        if (!docSnap.exists()) {
          setScanRecord(null);
          return;
        }

        const data = docSnap.data() as Partial<ReadinessScanRecord>;
        if (
          typeof data.websiteUrl !== 'string' ||
          typeof data.websiteHost !== 'string' ||
          (data.email !== null && typeof data.email !== 'string') ||
          typeof data.source !== 'string' ||
          !data.status ||
          !(data.status in statusLabelMap)
        ) {
          console.warn(
            `[CustomerDashboardPage] Invalid readiness scan document for user "${userId}". Data will be ignored.`
          );
          setScanRecord(null);
          return;
        }

        const status = data.status as ReadinessStatus;
        const storedProgress = typeof data.progressPercent === 'number' ? data.progressPercent : null;
        const progressPercent = storedProgress !== null ? Math.max(0, Math.min(100, storedProgress)) : statusDefaultProgress[status];

        setScanRecord({
          websiteUrl: data.websiteUrl,
          websiteHost: data.websiteHost,
          email: data.email ?? null,
          source: data.source,
          status,
          progressPercent,
        });
      },
      (error) => {
        console.error(`[CustomerDashboardPage] Failed to load readiness scan for user "${userId}":`, error);
        setIsScanLoading(false);
        const firestoreError = error as { code?: string };
        if (firestoreError.code === 'permission-denied') {
          setScanMessage({
            type: 'error',
            text: 'Permission denied loading scan status. Verify Firestore rules are deployed to the active database.',
          });
          return;
        }
        setScanMessage({ type: 'error', text: 'Unable to load your scan status right now.' });
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const resetWebsiteServiceForm = () => {
    setCapabilityStatementFile(null);
    setServiceSubmitMessage(null);
    setServiceForm({
      companyName: '',
      contactName: '',
      contactEmail: userEmail ?? '',
      contactPhone: '',
      websiteUrl: '',
      requestType: 'new_website',
      briefRequest: '',
    });
  };

  const closeWebsiteServiceModal = () => {
    setIsServiceModalOpen(false);
    if (!isServiceSubmitting) {
      resetWebsiteServiceForm();
    }
  };

  useEffect(() => {
    if (!isServiceModalOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isServiceSubmitting) {
        setIsServiceModalOpen(false);
        resetWebsiteServiceForm();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isServiceModalOpen, isServiceSubmitting, userEmail]);

  const handleReadinessSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setScanMessage(null);

    if (scanRecord) {
      setScanMessage({ type: 'error', text: 'You already submitted your Federal Readiness Scan request.' });
      return;
    }

    const normalizedUrl = normalizeWebsiteUrl(websiteInput);
    if (!normalizedUrl) {
      setScanMessage({ type: 'error', text: 'Please enter your business website URL.' });
      return;
    }

    let parsedUrl: URL;
    try {
      parsedUrl = new URL(normalizedUrl);
    } catch {
      setScanMessage({ type: 'error', text: 'Please enter a valid website URL.' });
      return;
    }

    const scanRef = doc(db, 'artifacts', appId, 'users', userId, 'federalReadinessScan', 'main');

    setIsSubmittingScan(true);
    try {
      await setDoc(scanRef, {
        websiteUrl: normalizedUrl,
        websiteHost: parsedUrl.hostname.toLowerCase(),
        email: userEmail ?? null,
        source: 'customer_dashboard_federal_readiness_scan',
        status: 'submitted',
        progressPercent: 20,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setWebsiteInput('');
      setScanMessage({ type: 'success', text: 'Request submitted. You can track progress here.' });
    } catch (error) {
      console.error(
        `[CustomerDashboardPage] Failed to submit readiness scan for user "${userId}" and website "${normalizedUrl}":`,
        error
      );
      const firestoreError = error as { code?: string };
      if (firestoreError.code === 'permission-denied') {
        setScanMessage({
          type: 'error',
          text: 'Permission denied submitting scan. Confirm Firestore rules are deployed to the same database used by this app.',
        });
        return;
      }
      setScanMessage({ type: 'error', text: 'Unable to submit right now. Please try again later.' });
    } finally {
      setIsSubmittingScan(false);
    }
  };

  const handleServiceFieldChange =
    (field: keyof typeof serviceForm) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setServiceForm((previous) => ({ ...previous, [field]: event.target.value }));
    };

  const handleCapabilityStatementFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setCapabilityStatementFile(null);
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setServiceSubmitMessage({
        type: 'error',
        text: 'Capability statement must be a PDF file.',
      });
      event.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setServiceSubmitMessage({
        type: 'error',
        text: 'Capability statement must be 10MB or less.',
      });
      event.target.value = '';
      return;
    }

    setServiceSubmitMessage(null);
    setCapabilityStatementFile(file);
  };

  const handleWebsiteServiceRequestSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setServiceSubmitMessage(null);

    const normalizedServiceWebsiteUrl = normalizeWebsiteUrl(serviceForm.websiteUrl);
    if (serviceForm.websiteUrl.trim()) {
      try {
        new URL(normalizedServiceWebsiteUrl);
      } catch {
        setServiceSubmitMessage({
          type: 'error',
          text: 'Please provide a valid website URL or leave it blank.',
        });
        return;
      }
    }

    setIsServiceSubmitting(true);
    try {
      const requestsCollectionRef = collection(
        db,
        'artifacts',
        appId,
        'public',
        'data',
        'websiteServiceRequests'
      );
      const requestDocRef = doc(requestsCollectionRef);
      let capabilityStatement:
        | {
            fileName: string;
            fileSize: number;
            fileType: string;
            storagePath: string;
          }
        | null = null;

      if (capabilityStatementFile) {
        const safeFileName = capabilityStatementFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const normalizedPdfFileName = safeFileName.toLowerCase().endsWith('.pdf')
          ? safeFileName.toLowerCase()
          : `${safeFileName.toLowerCase()}.pdf`;
        const storagePath = `artifacts/${appId}/website-service-requests/${requestDocRef.id}/${Date.now()}-${normalizedPdfFileName}`;
        const statementRef = ref(storage, storagePath);
        await uploadBytes(statementRef, capabilityStatementFile, {
          contentType: 'application/pdf',
        });

        capabilityStatement = {
          fileName: capabilityStatementFile.name,
          fileSize: capabilityStatementFile.size,
          fileType: 'application/pdf',
          storagePath,
        };
      }

      await setDoc(requestDocRef, {
        companyName: serviceForm.companyName.trim(),
        contactName: serviceForm.contactName.trim(),
        contactEmail: serviceForm.contactEmail.trim().toLowerCase(),
        contactPhone: serviceForm.contactPhone.trim(),
        websiteUrl: normalizedServiceWebsiteUrl || null,
        requestType: serviceForm.requestType,
        briefRequest: serviceForm.briefRequest.trim() || null,
        source: 'customer_dashboard_website_service_cta',
        adminStatus: 'new',
        capabilityStatement,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setServiceSubmitMessage({
        type: 'success',
        text: 'Request submitted. Our team will contact you with next steps.',
      });
      setTimeout(() => {
        setIsServiceModalOpen(false);
        resetWebsiteServiceForm();
      }, 900);
    } catch (error) {
      console.error(
        `[CustomerDashboardPage] Failed to submit website service request for company "${serviceForm.companyName.trim()}". Request was not saved:`,
        error
      );
      setServiceSubmitMessage({
        type: 'error',
        text: 'Unable to submit your request right now. Please try again in a moment.',
      });
    } finally {
      setIsServiceSubmitting(false);
    }
  };

  return (
    <main id="main-content" className="bg-gray-50" role="main" aria-label="Customer dashboard">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="rounded-2xl border border-smb-slate-100 bg-white shadow-sm p-6 sm:p-8">
          <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-mid">
            Customer Dashboard
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-smb-ink">
            Welcome back, {displayName}
          </h1>
          <p className="mt-3 text-smb-slate-600 max-w-2xl">
            This is your simple workspace for tracking what to do next. Start with the actions below and
            keep your subcontracting pipeline moving each week.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {quickActionsConfig.map(({ id, title, description, icon: Icon, cta, comingSoon }) => {
            const isProfile = id === 'profile';
            const isProfileLink = isProfile && !comingSoon;
            return (
              <article
                key={id}
                className="rounded-2xl border border-smb-slate-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-11 h-11 rounded-xl bg-smb-primary-light/20 text-smb-primary flex items-center justify-center">
                  <Icon size={20} aria-hidden="true" />
                </div>
                <h2 className="mt-4 text-lg font-semibold text-smb-ink">{title}</h2>
                <p className="mt-2 text-sm text-smb-slate-600">{description}</p>
                {comingSoon ? (
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-smb-slate-500">
                    Coming soon
                  </p>
                ) : isProfileLink ? (
                  <Link
                    to="/dashboard/profile"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-smb-primary hover:text-smb-primary-mid transition-colors text-left"
                  >
                    {cta}
                    <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                ) : (
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-smb-slate-500">
                    Coming soon
                  </p>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-smb-slate-100 bg-white shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setIsProcessExpanded((prev) => !prev)}
            className="w-full flex items-center justify-between gap-3 p-5 sm:p-6 text-left hover:bg-smb-slate-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-smb-primary-mid focus:ring-inset rounded-2xl"
            aria-expanded={isProcessExpanded}
            aria-controls="process-at-a-glance-content"
            id="process-at-a-glance-heading"
          >
            <div>
              <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-mid">
                Process at a Glance
              </p>
              <p className="mt-1 text-sm text-smb-slate-600">
                Our process: 6 steps from capability review to meeting prep
              </p>
            </div>
            <span className="shrink-0 rounded-lg p-2 text-smb-slate-600 bg-smb-slate-100">
              {isProcessExpanded ? (
                <ChevronUp size={20} aria-hidden="true" />
              ) : (
                <ChevronDown size={20} aria-hidden="true" />
              )}
            </span>
          </button>
          <div
            id="process-at-a-glance-content"
            role="region"
            aria-labelledby="process-at-a-glance-heading"
            className={isProcessExpanded ? 'border-t border-smb-slate-100' : 'sr-only'}
          >
            {isProcessExpanded && (
              <div className="p-5 sm:p-6 pt-4 sm:pt-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {processStepsConfig.map(({ number, title, description, icon: Icon }) => (
                    <article
                      key={number}
                      className="rounded-xl border border-smb-slate-100 bg-smb-slate-50/30 p-4 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-smb-primary-light/20 text-smb-primary flex items-center justify-center shrink-0">
                          <Icon size={20} aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base font-semibold text-smb-ink">
                            {number}. {title}
                          </h3>
                          <p className="mt-1 text-sm text-smb-slate-600">{description}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 rounded-2xl overflow-hidden shadow-sm border border-indigo-200 bg-gradient-to-br from-indigo-700 via-indigo-600 to-blue-600">
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                <Bot size={26} className="text-white" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-indigo-200">
                  <Zap size={12} aria-hidden="true" />
                  Human-Led Service
                </p>
                <h2 className="mt-1 text-2xl sm:text-3xl font-bold text-white">
                  Virtual Assistant for Federal Contracting
                </h2>
                <p className="mt-2 text-sm sm:text-base text-indigo-100 max-w-2xl">
                  Our human-led VA handles the time-consuming tasks — from researching primes and
                  drafting outreach messages to structuring follow-up cadences and preparing you for
                  meetings. Spend your time closing, not chasing.
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { icon: MessageSquare, label: 'Outreach drafting', detail: 'Personalized prime messaging in minutes' },
                    { icon: ClipboardList, label: 'Follow-up cadence', detail: 'Structured sequences so nothing falls through' },
                    { icon: Calendar, label: 'Meeting prep', detail: 'Prep briefs before every call' },
                  ].map(({ icon: Icon, label, detail }) => (
                    <div
                      key={label}
                      className="flex items-start gap-3 rounded-xl bg-white/10 px-4 py-3"
                    >
                      <Icon size={18} className="text-indigo-200 shrink-0 mt-0.5" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-semibold text-white">{label}</p>
                        <p className="mt-0.5 text-xs text-indigo-200">{detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => onOpenStrategyCall?.()}
                    className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50 transition-colors shadow-sm"
                  >
                    <Calendar size={16} aria-hidden="true" />
                    Book a meeting
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-smb-slate-100 bg-white shadow-sm p-6 sm:p-8">
          <p className="text-sm font-semibold tracking-wide uppercase text-smb-primary-mid">
            Federal Readiness Scan
          </p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-smb-ink">
            {scanRecord
              ? scanRecord.status === 'completed'
                ? 'Your scan is complete — view your results'
                : 'Track your scan progress'
              : 'Submit your website once and track progress'}
          </h2>

          {isScanLoading ? (
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-smb-slate-600">
              <Loader2 size={16} className="animate-spin" />
              Loading your scan status...
            </div>
          ) : scanRecord ? (
            <div className="mt-5 rounded-xl border border-smb-slate-100 bg-smb-slate-100/40 p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-smb-slate-600">Submitted domain</p>
                  <p className="text-base font-semibold text-smb-ink">{scanRecord.websiteHost}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-smb-primary-light/20 px-3 py-1 text-xs font-semibold text-smb-primary">
                  <CheckCircle2 size={14} aria-hidden="true" />
                  {statusLabelMap[scanRecord.status]}
                </span>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-smb-slate-600 mb-1.5">
                  <span>Progress</span>
                  <span>{scanRecord.progressPercent}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-smb-slate-100 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-smb-primary transition-all duration-300"
                    style={{ width: `${scanRecord.progressPercent}%` }}
                  />
                </div>
              </div>
              <p className="mt-3 text-xs text-smb-slate-600">
                You can only submit once per account. Status updates here as your scan moves forward.
              </p>
              {scanRecord.status === 'completed' && (
                <div className="mt-4 pt-4 border-t border-smb-slate-200">
                  <Link
                    to="/dashboard/federal-readiness-results"
                    className="inline-flex items-center gap-2 rounded-lg bg-smb-primary-mid px-4 py-2.5 text-sm font-semibold text-white hover:bg-smb-primary transition-colors"
                  >
                    View results
                    <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleReadinessSubmit} className="mt-5 space-y-3 max-w-xl">
              <label className="block text-sm font-medium text-smb-slate-700" htmlFor="readiness-website">
                Business website domain
              </label>
              <div className="relative">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-smb-slate-500" />
                <input
                  id="readiness-website"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  value={websiteInput}
                  onChange={(event) => setWebsiteInput(event.target.value)}
                  placeholder="yourcompany.com"
                  className="w-full rounded-lg border border-smb-slate-100 bg-white pl-10 pr-3 py-2.5 text-sm text-smb-ink placeholder:text-smb-slate-500 focus:outline-none focus:ring-2 focus:ring-smb-primary-mid"
                  required
                  disabled={isSubmittingScan}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmittingScan}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmittingScan ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Federal Readiness Scan'
                )}
              </button>
            </form>
          )}

          {scanMessage && (
            <p className={`mt-3 text-sm ${scanMessage.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
              {scanMessage.text}
            </p>
          )}

          <div className="mt-8 rounded-xl border border-blue-100 bg-blue-50/70 p-4 sm:p-5">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
              <Sparkles size={14} />
              Need website help?
            </p>
            <h3 className="mt-2 text-lg font-bold text-smb-ink">
              No website yet or need enhancements?
            </h3>
            <p className="mt-1 text-sm text-smb-slate-600">
              Submit a quick request and our team will contact you to gather full details.
            </p>
            <button
              type="button"
              onClick={() => setIsServiceModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
            >
              Request Website Help
            </button>
          </div>
        </div>
      </section>

      {isServiceModalOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="website-service-modal-title"
          onClick={(event) => {
            if (event.target === event.currentTarget && !isServiceSubmitting) {
              closeWebsiteServiceModal();
            }
          }}
        >
          <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-700 to-blue-600 px-5 py-4">
              <div>
                <h3 id="website-service-modal-title" className="text-lg font-bold text-white">
                  Website Development & Enhancement Request
                </h3>
                <p className="text-sm text-blue-100">
                  Submit a quick request now. We will follow up to collect full project details.
                </p>
              </div>
              <button
                type="button"
                onClick={closeWebsiteServiceModal}
                disabled={isServiceSubmitting}
                className="rounded-lg p-2 text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 disabled:opacity-50"
                aria-label="Close website service request modal"
              >
                <X size={20} />
              </button>
            </div>
            <form
              className="overflow-y-auto max-h-[calc(90vh-84px)]"
              onSubmit={handleWebsiteServiceRequestSubmit}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Company name *</label>
                  <div className="relative">
                    <Building2
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      required
                      type="text"
                      value={serviceForm.companyName}
                      onChange={handleServiceFieldChange('companyName')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Acme Federal Solutions"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Contact name *</label>
                  <div className="relative">
                    <User2
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      required
                      type="text"
                      value={serviceForm.contactName}
                      onChange={handleServiceFieldChange('contactName')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Jane Smith"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Contact email *</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      required
                      type="email"
                      value={serviceForm.contactEmail}
                      onChange={handleServiceFieldChange('contactEmail')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="name@company.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Phone number *</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      required
                      type="tel"
                      value={serviceForm.contactPhone}
                      onChange={handleServiceFieldChange('contactPhone')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Current website (optional)
                  </label>
                  <div className="relative">
                    <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={serviceForm.websiteUrl}
                      onChange={handleServiceFieldChange('websiteUrl')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="yourcompany.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Request type *</label>
                  <select
                    required
                    value={serviceForm.requestType}
                    onChange={handleServiceFieldChange('requestType')}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="new_website">New website development</option>
                    <option value="website_enhancement">Website enhancement</option>
                    <option value="redesign_and_enhancement">Redesign + enhancement</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Tell us what you need (optional)
                  </label>
                  <textarea
                    rows={3}
                    value={serviceForm.briefRequest}
                    onChange={handleServiceFieldChange('briefRequest')}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Share a quick note. We'll gather full details during follow-up."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Capability statement PDF (optional)
                  </label>
                  <div className="rounded-xl border border-dashed border-blue-300 bg-blue-50/60 px-4 py-4">
                    <div className="flex items-start gap-3">
                      <FileText size={18} className="text-blue-700 mt-0.5" />
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={handleCapabilityStatementFileChange}
                          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white hover:file:bg-blue-700"
                        />
                        <p className="mt-1 text-xs text-gray-600">PDF format only, up to 10MB.</p>
                        {capabilityStatementFile && (
                          <p className="mt-1 text-xs font-medium text-green-700">
                            Selected: {capabilityStatementFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 px-5 py-4 bg-gray-50">
                {serviceSubmitMessage && (
                  <p
                    className={`mb-3 text-sm font-medium ${
                      serviceSubmitMessage.type === 'success' ? 'text-green-700' : 'text-red-600'
                    }`}
                  >
                    {serviceSubmitMessage.text}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeWebsiteServiceModal}
                    disabled={isServiceSubmitting}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isServiceSubmitting}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isServiceSubmitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
